import styled from 'styled-components';

import { useAuthContext } from '@providers/AuthProvider';
import { Button } from '@components/form-elements/buttons/Button';
import { CustomTable } from '@components/Table';
import { TrashBin } from '@components/icons/TrashBin';
import { Add as AddIcon } from '@components/icons/Add';
import { TLicense } from 'app/types/entities/TLicense';
import { TUser } from 'app/types/entities/TUser';
import { getTranscriptUnlockTypeName } from '@helpers/getTranscriptUnlockTypeName';
import { Pencil as PencilIcon } from '@components/icons/Pencil';
import { Header } from '@components/Header';
import { routes } from '@routes';
import { LinearProgressLoader } from '@components/spinners/LinearProgressLoader';
import { usePageContext } from '../../providers/PageProvider';
import { Select } from '@components/form-elements/Select';
import { pluralize } from '@helpers/pluralize';

const CellContent = styled.div`
  margin: 0;
  padding: 15px 0;

  svg {
    cursor: pointer;
    padding: 5px;
    height: 30px;
    width: 30px;
  }

  svg.add {
    stroke: #40608f;
  }

  svg.delete {
    path {
      fill: #40608f;
    }
  }
`;

const CellContentFlex = styled(CellContent)`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const StyledButton = styled(Button)`
  width: 90px;
  display: flex;
  grid-gap: 5px;
  align-items: center;
  margin-right: 12px;
  justify-content: center;
`;

const MainContent = styled.main`
  padding: 8px 30px 70px;
  min-height: calc(100vh - 120px);

  td {
    vertical-align: top;
  }

  .details {
    padding: 15px 0 20px;
    display: flex;
    gap: 25px;
    align-items: center;

    * {
      flex: 0 0 auto;
    }

    button {
      width: auto;
    }

    .select {
      width: 180px;
      line-height: 14px;
      box-shadow: #cadcf8 0 0 0 1px;
      :hover {
        box-shadow: #2f5d87 0 0 0 1px;
      }
    }
    .select-wrapper {
      width: 180px;
      .optionsWrapper {
        width: 180px;
        top: -8px;
      }
      .icon--opened {
        right: 0;
      }
      .icon--closed {
        right: 15px;
      }
    }

    .left {
      color: #b4b9d9;

      .name {
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: 150%;
        text-transform: capitalize;
      }

      .price {
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 150%;
        text-transform: capitalize;
      }
    }

    .divider {
      border-right: 1px solid #d5def2;
      height: 40px;
    }

    .count {
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 40px;
      color: #858dbd;
      border: 1px solid #d5def2;
      border-radius: 10px;
      padding: 0 16px;
      height: 40px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      .bold {
        font-weight: bold;
      }
    }
  }

  .pencil {
    cursor: pointer;

    :hover {
      path {
        fill: #858dbd;
      }
    }
  }
`;

const AvatarBlock = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;

  .avatar {
    width: 30px;
    height: 30px;
    background: #cadcf8;
    border-radius: 5px;
  }

  .avatar-name {
    width: 30px;
    height: 30px;
    background: #cadcf8;
    border-radius: 5px;
    justify-content: center;
    display: flex;
    align-items: center;
    color: #ffffff;
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 30px;
    text-transform: capitalize;
  }
`;

export const Content = () => {
  const {
    isLoading,
    search,
    setSearch,
    removeAssignee,
    editTranscriptUnlockType,
    editAllTranscriptUnlockTypes,
    editBackupRecordingFlag,
    setGlobalAS1Backup,
    unlockAITranscripts,
    onAddUserClick,
    assignedCount,
    unassignedCount,
    subscription,
    plan,
    globalLicensesBackupEnabledValue,
    licenses,
    openAS1BackupTerms,
  } = usePageContext();
  const { organization } = useAuthContext();

  if (!plan || !subscription) {
    return null;
  }

  const renderAvatar = (member: TUser) => {
    if (!member) {
      return '';
    }
    const avatarName = `${member?.name[0]}${member?.lastname[0]}`;
    return (
      <AvatarBlock>
        {member?.avatar ? (
          <img className="avatar" src={member.avatar} alt="" />
        ) : (
          <div className="avatar-name">{avatarName}</div>
        )}
      </AvatarBlock>
    );
  };

  const tableHead = [
    {
      headComponent: () => <td>ID</td>,
      render: (data: TLicense) => <CellContent>{data.id}</CellContent>,
    },
    {
      headComponent: () => <td>Name</td>,
      render: (data: TLicense) => {
        if (!data.assignee) {
          return <CellContent>Unassigned</CellContent>;
        }
        return (
          <CellContentFlex>
            {renderAvatar(data.assignee)}
            {data.assignee?.name} {data.assignee?.lastname}
          </CellContentFlex>
        );
      },
    },
    {
      headComponent: () => <td style={{ width: '50%' }}>Contacts</td>,
      render: (data: TLicense) => {
        if (!data.assignee) {
          return <CellContent></CellContent>;
        }
        return (
          <CellContent>
            <div>{data.assignee.email}</div>
            <div>{data.assignee.phone}</div>
          </CellContent>
        );
      },
    },
    {
      headComponent: () => (
        <td style={{ width: '50%' }}>
          <div className="flex gap-2 align-middle">Transcript Unlock Type</div>
        </td>
      ),
      render: (data: TLicense) => {
        return (
          <CellContent>
            <div className="flex gap-1 align-middle">
              {getTranscriptUnlockTypeName(data.transcriptUnlockType)}
              {subscription?.allowedToChooseTranscriptUnlockType && data.assigneeId ? (
                <PencilIcon
                  className="pencil"
                  onClick={() => editTranscriptUnlockType(data.id, data.transcriptUnlockType ?? '')}
                />
              ) : null}
            </div>
          </CellContent>
        );
      },
    },
    {
      headComponent: () => <td style={{ width: '1%' }}></td>,
      render: (data: TLicense) => {
        if (!data.assignee) {
          return (
            <CellContent>
              <AddIcon className="add" onClick={() => onAddUserClick(data.id)} />
            </CellContent>
          );
        }
        return (
          <CellContent>
            <TrashBin className="delete" onClick={() => removeAssignee(data.id)} />
          </CellContent>
        );
      },
    },
  ];

  if (organization?.featureFlags?.AS_ONE_BACKUP_RECORDING) {
    tableHead.splice(4, 0, {
      headComponent: () => (
        <td style={{ width: '50%' }}>
          <div className="flex gap-2 align-middle">AS1 Backup</div>
        </td>
      ),
      render: (data: TLicense) => {
        return (
          <CellContent>
            <div className="flex gap-1 align-middle">
              {data.asOneBackupRecordingAllowed ? 'On' : 'Off'}
              {data.assigneeId ? (
                <PencilIcon
                  className="pencil"
                  onClick={() => editBackupRecordingFlag(data.id, !!data.asOneBackupRecordingAllowed)}
                />
              ) : null}
            </div>
          </CellContent>
        );
      },
    });
  }

  const headerLeftSideContent = [];
  if (organization?.permissions?.manageLicenses) {
    headerLeftSideContent.push(
      <div className="flex flex-row">
        <StyledButton
          key="button"
          size="small"
          disabled={(unassignedCount ?? 0) < 1}
          onClick={() => onAddUserClick(null)}
        >
          + Add User
        </StyledButton>
      </div>,
    );
  }

  return (
    <>
      <Header
        title={'Subscription Details'}
        setSearch={setSearch}
        search={search}
        leftSideContent={headerLeftSideContent}
        backRoute={routes.billingAccount.make(organization?.slug ?? '')}
      />

      <LinearProgressLoader active={isLoading} />
      <MainContent>
        <div className="details">
          <div className="left">
            <div className="name">{plan.name}</div>
            <div className="price">${Number(plan.price).toFixed(2)} Per License Monthly</div>
          </div>
          <div className="divider"></div>
          <div className="count">
            You have{' '}
            <span className="bold">
              {unassignedCount} out of {(assignedCount ?? 0) + (unassignedCount ?? 0)}
            </span>{' '}
            {pluralize((assignedCount ?? 0) + (unassignedCount ?? 0), 'license', 'licenses')} available to assign
          </div>
          <div className="divider"></div>
          {subscription?.allowedToChooseTranscriptUnlockType ? (
            <Button size="small" variant="outlined" onClick={() => editAllTranscriptUnlockTypes()}>
              Update all Unlock types
            </Button>
          ) : (
            <Button size="small" variant="outlined" onClick={() => unlockAITranscripts()}>
              Unlock AI Transcripts
            </Button>
          )}
          {organization?.featureFlags?.AS_ONE_BACKUP_RECORDING ? (
            <>
              <div className="divider"></div>
              <Select
                className="select-wrapper"
                onClick={
                  !organization?.asOneBackupRecordingTermsAccepted
                    ? (e) => {
                        e.stopPropagation();
                        openAS1BackupTerms();
                      }
                    : undefined
                }
                onChange={(_, v) => setGlobalAS1Backup(v)}
                label={`Global AS1 Backup: ${globalLicensesBackupEnabledValue}`}
                options={[
                  { label: 'On', value: '1' },
                  { label: 'Off', value: '0' },
                ]}
              />
            </>
          ) : null}
        </div>

        <CustomTable
          head={tableHead}
          data={licenses.filter(
            (license) =>
              license.id.includes(search.toLowerCase()) ||
              `${license.assignee?.name} ${license.assignee?.lastname}`.toLowerCase().includes(search.toLowerCase()) ||
              license.assignee?.email.toLowerCase().includes(search.toLowerCase()) ||
              license.assignee?.phone.toLowerCase().includes(search.toLowerCase()),
          )}
        />
      </MainContent>
    </>
  );
};
